import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FaUser } from 'react-icons/fa';
import { getAppChromeMeta, getAvatarInitials } from './appChromeMeta';
import {
  getAppChromeKickerOverride,
  subscribeAppChromeKickerOverride,
} from './appChromeKicker';
import { CachedImage } from '../common/CachedImage';
import './AppChromeHeader.css';

const YONA_BRAND_MARK_SRC = `${process.env.PUBLIC_URL || ''}/brand/yona-bear-2.png`;

export function AppChromeHeader({
  currentPage,
  pathname = '',
  search = '',
  profilePhotoUrl = '',
  profileDisplayName = 'Profile',
  unreadNotificationsTotal = 0,
  onStandCount = 0,
  onNavigate,
}) {
  const headerRef = useRef(null);
  const [kickerOverride, setKickerOverride] = useState(getAppChromeKickerOverride);

  useEffect(() => subscribeAppChromeKickerOverride(setKickerOverride), []);

  useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;

    const apply = () => {
      const height = Math.ceil(el.getBoundingClientRect().height);
      if (height > 0) {
        document.documentElement.style.setProperty('--y-app-chrome-height', `${height}px`);
      }
    };

    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, []);

  const { title, kicker } = getAppChromeMeta(currentPage, pathname, search);
  const displayKicker = kickerOverride || kicker;
  const initials = getAvatarInitials(profileDisplayName);
  const unreadLabel = unreadNotificationsTotal > 99 ? '99+' : String(unreadNotificationsTotal);

  return (
    <header ref={headerRef} className="app-chrome-header" data-testid="app-chrome-header">
      <div className="app-chrome-header__brand-mark" aria-hidden="true">
        <img src={YONA_BRAND_MARK_SRC} alt="" className="app-chrome-header__brand-img" />
      </div>

      <div className="app-chrome-header__titles">
        <h1 className="app-chrome-header__title">{title}</h1>
        {displayKicker ? <p className="app-chrome-header__kicker">{displayKicker}</p> : null}
      </div>

      <div className="app-chrome-header__actions">
        <div
          className="app-chrome-header__on-stand"
          data-testid="app-chrome-on-stand"
          title="Hunters currently in the woods"
        >
          <span className="app-chrome-header__on-stand-dot" aria-hidden="true" />
          <span className="app-chrome-header__on-stand-label">
            {onStandCount} IN THE WOODS
          </span>
        </div>

        <button
          type="button"
          className="app-chrome-header__avatar"
          onClick={() => onNavigate('/profile')}
          aria-label="Open profile hub"
          data-testid="app-chrome-avatar"
        >
          <span className="app-chrome-header__avatar-face">
            {profilePhotoUrl ? (
              <CachedImage src={profilePhotoUrl} alt="" className="app-chrome-header__avatar-img" />
            ) : (
              <span className="app-chrome-header__avatar-initials" aria-hidden="true">
                {initials === '?' ? <FaUser size={14} /> : initials}
              </span>
            )}
          </span>
          {unreadNotificationsTotal > 0 ? (
            <span className="app-chrome-header__avatar-badge" data-testid="app-chrome-avatar-badge">
              {unreadLabel}
            </span>
          ) : null}
        </button>
      </div>
    </header>
  );
}

AppChromeHeader.propTypes = {
  currentPage: PropTypes.string.isRequired,
  pathname: PropTypes.string,
  search: PropTypes.string,
  profilePhotoUrl: PropTypes.string,
  profileDisplayName: PropTypes.string,
  unreadNotificationsTotal: PropTypes.number,
  onStandCount: PropTypes.number,
  onNavigate: PropTypes.func.isRequired,
};
